import { useForm } from 'react-hook-form';
import { PrimaryButton, RadioButton, SecondaryButton, Typography } from '@weareopenr/component-library';
import { IPublishFormValues, PublishType } from '../../types';
import styles from './SelectPublishType.module.scss';
import { ButtonContainer } from '../../../ButtonContainer/ButtonContainer';
import { ModalTitle } from '../../../ModalTitle/ModalTitle';
import { useVenues } from '../../../../hooks/useVenues/useVenues';
import WarningBanner from '../../../WarningBanner/WarningBanner';
import { IntegrationType } from '../../../../constants';
import { getVenuesForIntegrationType } from '../../../../helpers/getVenuesForIntegrationType/getVenuesForIntegrationType';

const ButtonLabel = ({ name, description }: { name: string; description: string }) => {
  return (
    <div className={styles['radio-button-label']}>
      <Typography weight="semi-bold">{name}</Typography>
      <Typography>{description}</Typography>
    </div>
  );
};

export const SelectPublishType = ({
  defaultValues,
  closeModal,
  submitDataToParent,
}: {
  defaultValues: IPublishFormValues;
  closeModal: () => void;
  submitDataToParent: (data: IPublishFormValues, nextStep?: number) => void;
}) => {
  const { data: allVenues, isLoading } = useVenues();
  const venues = getVenuesForIntegrationType(allVenues, IntegrationType.POINT_OF_SALE_MANAGEMENT);
  const { register, handleSubmit, watch } = useForm<Pick<IPublishFormValues, 'type'>>({
    defaultValues: {
      type: defaultValues.type,
    },
  });
  const type = watch('type');
  const onSubmit = (data: IPublishFormValues) => {
    submitDataToParent(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <ModalTitle title="Publish data" subTitle="Select which data you’d like to publish." />
      {venues.length === 0 && !isLoading && (
        <div className={styles['no-venues-container']}>
          <WarningBanner
            text="To publish pricing data you need at least one venue. Create a venue to get started"
            storageKey="productPublish"
          />
        </div>
      )}
      <RadioButton name="type" value={PublishType.PRODUCT} register={register as any}>
        <ButtonLabel
          name="Product data"
          description="Publish product data to all venues. This includes all product details such as names and other product attributes."
        />
      </RadioButton>
      <RadioButton
        name="type"
        value={PublishType.PRICING}
        register={register as any}
        disabled={venues.length === 0 || isLoading}
      >
        <ButtonLabel
          name="Pricing data"
          description="Publish pricing data to a selection of venues. This includes prices for selected products based on your price scheme."
        />
      </RadioButton>
      <ButtonContainer
        left={
          <SecondaryButton onClick={closeModal} size="full-width" buttonStyle="outline">
            Close
          </SecondaryButton>
        }
        right={
          <PrimaryButton
            type="submit"
            size="full-width"
            disabled={(venues.length === 0 && type === PublishType.PRICING) || isLoading}
          >
            Continue
          </PrimaryButton>
        }
      />
    </form>
  );
};
